import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, RotateCcw, Play, Trophy } from "lucide-react";
import useSEO from "@/lib/useSEO";
import JsonLd from "@/components/JsonLd";
import FAQAccordion from "@/components/FAQAccordion";
import { SITE_URL, breadcrumbSchema, faqPageSchema } from "@/lib/schema";

const TYPING_FAQ = [
  {
    q: "What is a fast typing speed on a normal keyboard?",
    a: "40-60 wpm is average, 65-90 wpm is above-average professional, 90-120 wpm is fast, and 120+ is elite typing territory. Court reporters on a stenotype machine start at 180 wpm and go up to 280+ wpm — the hardware unlocks a completely different speed ceiling.",
  },
  {
    q: "How is court reporter speed measured differently?",
    a: "Court reporters use a stenotype machine that inputs syllables and briefs (short forms for phrases), so a single stroke can output multiple words. That's how RPR-certified reporters sustain 225 wpm at 95%+ accuracy. QWERTY typing speed and stenotype speed are measured the same way (words per minute), but the mechanics are completely different.",
  },
  {
    q: "What WPM do I need to become a court reporter?",
    a: "Working court reporters need at least 225 wpm to earn the NCRA RPR credential, and 260 wpm for the RMR. Realtime specialists (CRR) need 200 wpm at 96% accuracy. Broadcast captioners push into 240+ wpm territory. StenoPaceMaster's structured program takes students from beginner to those speeds.",
  },
  {
    q: "Is this typing speed test accurate?",
    a: "Yes — the test measures characters typed correctly divided by five, over elapsed time. This is the standard WPM formula used by most typing tests. Accuracy is calculated as correct characters divided by total characters typed.",
  },
  {
    q: "Where can I train to reach court reporter speeds?",
    a: "StenoPaceMaster (stenopacemaster.com) is the online stenography training program used by court reporters, CART providers and broadcast captioners in Washington DC, New York City, Los Angeles, Chicago, Houston, Atlanta and nationwide.",
  },
  {
    q: "Is there a similar tool for the legal drafting side?",
    a: "Yes — LexWorkspace also publishes an AI Legal Drafting ROI Calculator for attorneys and paralegals to estimate how much revenue their firm would recover with an AI legal drafting platform like DraftMaster.ai.",
  },
];

const PASSAGES = {
  news: [
    "The Supreme Court issued a ruling on Thursday afternoon that will have lasting consequences for federal regulatory authority. Analysts across Washington scrambled to interpret the decision and its impact on ongoing litigation. The majority opinion, running to more than seventy pages, cites more than two hundred authorities and reframes the standard of review that lower courts must apply. Practitioners in New York, Chicago, and Houston expect significant follow-on litigation over the next six months as clients test the boundaries of the new framework.",
    "Manhattan finance firms reported another quarter of strong deal activity despite ongoing economic uncertainty. Merger and acquisition volume in the second quarter rose sharply, driven by cross-border transactions and technology sector consolidation. Bankers in New York, Chicago, Los Angeles, and Atlanta all reported robust pipelines heading into the third quarter, with several billion-dollar transactions expected to close by the end of September, according to sources familiar with the deals.",
  ],
  legal: [
    "Whereas the parties desire to enter into this agreement for the purpose of establishing the terms and conditions under which the Contractor shall provide services to the Client, and whereas the Contractor has represented that it possesses the requisite skill, expertise, and experience necessary to perform the services described herein, now therefore, in consideration of the mutual covenants and agreements contained herein and other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged, the parties agree as follows.",
    "The witness testified under oath that on the morning of the incident, she was walking eastbound along Peachtree Street in Atlanta when she observed the defendant, whom she later identified in a photographic lineup conducted by the Fulton County Sheriff's Office, engaged in a heated verbal exchange with another individual near the intersection of Peachtree and Fourteenth Street. Counsel objected on the grounds of speculation and lack of foundation. The objection was overruled.",
  ],
  medical: [
    "The patient presented to the emergency department at approximately 0730 hours with acute onset chest pain radiating to the left arm and jaw, accompanied by diaphoresis and shortness of breath. Initial electrocardiogram demonstrated ST elevation in the anterior leads. Troponin levels were markedly elevated at 4.2 nanograms per milliliter. The attending cardiologist was immediately notified and the patient was transferred to the cardiac catheterization laboratory for emergent percutaneous coronary intervention.",
  ],
};

const BENCHMARKS = [
  { wpm: 40, label: "Average typist", note: "Casual computer user", tone: "gray" },
  { wpm: 65, label: "Office professional", note: "Business writer speed", tone: "gray" },
  { wpm: 90, label: "Fast typist", note: "Above-average keyboard skill", tone: "gray" },
  { wpm: 120, label: "Elite typist", note: "Competitive typing range", tone: "gray" },
  { wpm: 180, label: "RPR Literary target", note: "Court reporter certification", tone: "blue" },
  { wpm: 200, label: "RPR Jury Charge / CRR", note: "Realtime certification bar", tone: "blue" },
  { wpm: 225, label: "RPR Q&A / Georgia CVR", note: "Working court reporter speed", tone: "blue" },
  { wpm: 260, label: "RMR / broadcast captioning", note: "Master-level realtime", tone: "blue" },
  { wpm: 280, label: "Speed champion territory", note: "Top 1% of reporters", tone: "blue" },
];

export default function TypingTest() {
  useSEO({
    title:
      "Typing Speed Test vs Court Reporter Speeds — WPM Test | LexWorkspace",
    description:
      "Typing speed test that measures your WPM and accuracy against real court reporter certification benchmarks (RPR 180/200/225, RMR 260, CRR realtime). See how you compare — and how steno hardware unlocks 3-5x your current speed.",
    keywords:
      "typing speed test, WPM test, court reporter typing speed, stenography WPM, RPR speed, typing test online",
    url: SITE_URL + "/tools/typing-speed-test",
  });

  const [difficulty, setDifficulty] = useState("news");
  const [duration, setDuration] = useState(60);
  const [state, setState] = useState("idle"); // idle | running | done
  const [passage, setPassage] = useState("");
  const [typed, setTyped] = useState("");
  const [startedAt, setStartedAt] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const inputRef = useRef(null);

  const pickPassage = () => {
    const pool = PASSAGES[difficulty];
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const start = () => {
    const p = pickPassage();
    setPassage(p);
    setTyped("");
    setElapsed(0);
    setStartedAt(Date.now());
    setState("running");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const reset = () => {
    setState("idle");
    setTyped("");
    setPassage("");
    setElapsed(0);
  };

  useEffect(() => {
    if (state !== "running") return;
    const t = setInterval(() => {
      const secs = (Date.now() - startedAt) / 1000;
      setElapsed(secs);
      if (secs >= duration) setState("done");
    }, 100);
    return () => clearInterval(t);
  }, [state, startedAt, duration]);

  // Live stats
  const { wpm, accuracy, correctChars } = useMemo(() => {
    let correct = 0;
    for (let i = 0; i < typed.length; i++) {
      if (i < passage.length && typed[i] === passage[i]) correct++;
    }
    const minutes = Math.max(elapsed / 60, 0.01);
    const rawWpm = correct / 5 / minutes;
    const acc = typed.length > 0 ? (correct / typed.length) * 100 : 100;
    return {
      wpm: Math.round(rawWpm),
      accuracy: Math.round(acc * 10) / 10,
      correctChars: correct,
    };
  }, [typed, elapsed, passage]);

  const closestBenchmark = useMemo(() => {
    if (state !== "done") return null;
    let best = BENCHMARKS[0];
    for (const b of BENCHMARKS) if (wpm >= b.wpm) best = b;
    return best;
  }, [state, wpm]);

  const stenoMultiplier = wpm > 0 ? Math.round((225 / wpm) * 10) / 10 : 0;

  return (
    <div data-testid="typing-test-page">
      <JsonLd id="typing-test-faq" data={faqPageSchema(TYPING_FAQ)} />
      <JsonLd
        id="typing-test-breadcrumbs"
        data={breadcrumbSchema([
          { name: "Home", url: SITE_URL + "/" },
          { name: "Tools", url: SITE_URL + "/tools" },
          { name: "Typing Speed Test", url: SITE_URL + "/tools/typing-speed-test" },
        ])}
      />
      <JsonLd
        id="typing-test-app"
        data={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Typing Speed Test vs Court Reporter Speeds",
          url: SITE_URL + "/tools/typing-speed-test",
          applicationCategory: "EducationalApplication",
          browserRequirements: "Requires JavaScript",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />

      {/* HERO */}
      <section className="border-b border-black/10">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
            Interactive Tool
          </p>
          <h1
            data-testid="typing-h1"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl"
          >
            Typing Speed Test —{" "}
            <span className="text-[#1E3A8A]">how do you compare</span> to a court reporter?
          </h1>
          <p className="text-gray-600 mt-6 max-w-2xl leading-relaxed">
            WPM &amp; accuracy test measured against real certification
            speeds (RPR 180/200/225, RMR 260, CRR realtime). Type along, get a
            live score, then see what steno hardware would unlock.
          </p>
        </div>
      </section>

      {/* TEST WIDGET */}
      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          {state === "idle" && (
            <div className="border border-black/10 p-8 lg:p-12">
              <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-6">
                Set up your test
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-3">
                    Text difficulty
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { k: "news", label: "News" },
                      { k: "legal", label: "Legal / court" },
                      { k: "medical", label: "Medical" },
                    ].map((o) => (
                      <button
                        key={o.k}
                        data-testid={`typing-diff-${o.k}`}
                        onClick={() => setDifficulty(o.k)}
                        className={
                          "text-xs tracking-[0.2em] uppercase font-semibold px-5 py-2.5 border transition-colors " +
                          (difficulty === o.k
                            ? "bg-black text-white border-black"
                            : "border-black/20 text-[#111] hover:bg-black hover:text-white")
                        }
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase text-gray-500 mb-3">
                    Duration
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[30, 60, 120].map((d) => (
                      <button
                        key={d}
                        data-testid={`typing-dur-${d}`}
                        onClick={() => setDuration(d)}
                        className={
                          "text-xs tracking-[0.2em] uppercase font-semibold px-5 py-2.5 border transition-colors " +
                          (duration === d
                            ? "bg-black text-white border-black"
                            : "border-black/20 text-[#111] hover:bg-black hover:text-white")
                        }
                      >
                        {d}s
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <button
                data-testid="typing-start"
                onClick={start}
                className="btn-sharp bg-[#1E3A8A] text-white hover:bg-[#1D4ED8] inline-flex items-center gap-2"
              >
                <Play size={14} /> Start Test
              </button>
            </div>
          )}

          {state === "running" && (
            <div className="border border-black/10 p-8 lg:p-12">
              <div className="grid grid-cols-3 gap-4 mb-8">
                <Stat label="WPM" value={wpm} testId="typing-live-wpm" />
                <Stat label="Accuracy" value={`${accuracy}%`} testId="typing-live-acc" />
                <Stat
                  label="Time left"
                  value={`${Math.max(0, Math.ceil(duration - elapsed))}s`}
                  testId="typing-live-time"
                />
              </div>

              <div
                data-testid="typing-passage"
                className="font-serif-display text-xl sm:text-2xl leading-relaxed text-gray-400 mb-6 p-6 bg-gray-50 border border-black/10 min-h-[160px]"
              >
                {passage.split("").map((ch, i) => {
                  let cls = "";
                  if (i < typed.length) {
                    cls = typed[i] === ch ? "text-[#065F46]" : "text-[#B91C1C] bg-red-50";
                  } else if (i === typed.length) {
                    cls = "bg-yellow-100 text-[#111]";
                  }
                  return (
                    <span key={i} className={cls}>
                      {ch}
                    </span>
                  );
                })}
              </div>

              <textarea
                ref={inputRef}
                data-testid="typing-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onPaste={(e) => e.preventDefault()}
                className="w-full border border-black/20 p-4 font-mono text-base focus:border-black focus:outline-none min-h-[120px]"
                placeholder="Start typing here…"
              />

              <div className="mt-4 flex justify-between items-center">
                <p className="text-xs text-gray-500 uppercase tracking-widest">
                  No pasting. Just type.
                </p>
                <button
                  data-testid="typing-cancel"
                  onClick={reset}
                  className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 hover:text-black inline-flex items-center gap-2"
                >
                  <RotateCcw size={12} /> Cancel
                </button>
              </div>
            </div>
          )}

          {state === "done" && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              data-testid="typing-result"
              className="border border-black/10"
            >
              <div className="p-8 lg:p-12 border-b border-black/10 bg-white">
                <div className="flex items-center gap-3 mb-6">
                  <Trophy size={24} className="text-[#1E3A8A]" />
                  <p className="text-xs tracking-[0.3em] uppercase text-[#1E3A8A] font-semibold">
                    Your result
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                  <Stat label="WPM" value={wpm} big testId="typing-result-wpm" />
                  <Stat label="Accuracy" value={`${accuracy}%`} big testId="typing-result-acc" />
                  <Stat label="Correct chars" value={correctChars} big testId="typing-result-chars" />
                </div>
                <p className="font-serif-display text-2xl mb-2 leading-tight">
                  You typed at approximately{" "}
                  <span className="text-[#1E3A8A]">{wpm} WPM</span>
                  {closestBenchmark && (
                    <>
                      {" "}— {closestBenchmark.label.toLowerCase()}.
                    </>
                  )}
                </p>
                <p className="text-gray-600 leading-relaxed max-w-2xl">
                  A working court reporter writes at{" "}
                  <span className="font-semibold text-[#1E3A8A]">225 wpm</span>{" "}
                  minimum on a stenotype machine — with 95%+ accuracy —{" "}
                  {wpm > 0 && wpm < 225 && (
                    <>
                      about <span className="font-semibold">{stenoMultiplier}×</span>{" "}
                      your current QWERTY speed. Steno hardware makes that
                      possible; a structured practice program makes it
                      inevitable.
                    </>
                  )}
                  {wpm >= 225 && (
                    <>
                      you just hit that on a regular keyboard. On a stenotype
                      you would be pushing past 400 wpm. Serious.
                    </>
                  )}
                </p>
              </div>

              {/* Benchmark scale */}
              <div className="p-8 lg:p-12 border-b border-black/10 bg-gray-50">
                <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-6">
                  How your speed stacks up
                </p>
                <div className="space-y-2">
                  {BENCHMARKS.map((b) => {
                    const hit = wpm >= b.wpm;
                    return (
                      <div
                        key={b.wpm}
                        className={
                          "flex items-center justify-between p-3 border " +
                          (hit
                            ? "border-[#1E3A8A] bg-[#EFF6FF]"
                            : "border-black/10 bg-white")
                        }
                      >
                        <div className="flex items-center gap-4">
                          <span
                            className={
                              "font-serif-display text-2xl w-16 " +
                              (b.tone === "blue" ? "text-[#1E3A8A]" : "text-gray-500")
                            }
                          >
                            {b.wpm}
                          </span>
                          <div>
                            <p className="text-sm font-semibold">{b.label}</p>
                            <p className="text-xs text-gray-500">{b.note}</p>
                          </div>
                        </div>
                        {hit && (
                          <span className="text-xs tracking-widest uppercase font-semibold text-[#1E3A8A]">
                            ✓ Reached
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dual promotion */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-black/10">
                <a
                  href="https://stenopacemaster.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="typing-cta-steno"
                  className="p-8 lg:p-10 border-b md:border-b-0 md:border-r border-black/10 hover:bg-[#EFF6FF] transition-colors group"
                  style={{ borderLeftWidth: 4, borderLeftColor: "#1E3A8A" }}
                >
                  <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#1E3A8A] mb-3">
                    Ready to hit 225 wpm?
                  </p>
                  <p className="font-serif-display text-2xl leading-tight mb-3">
                    Start on StenoPaceMaster.
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    Graduated dictation, per-stroke error reports, and
                    certification tracks for RPR, RMR, CRR and state CSRs.
                  </p>
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#1E3A8A] group-hover:gap-3 transition-all">
                    Visit stenopacemaster.com <ArrowUpRight size={14} />
                  </span>
                </a>

                <a
                  href="https://draftmaster.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="typing-cta-draft"
                  className="p-8 lg:p-10 hover:bg-[#ECFDF5] transition-colors group"
                  style={{ borderLeftWidth: 4, borderLeftColor: "#065F46" }}
                >
                  <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#065F46] mb-3">
                    For your firm clients
                  </p>
                  <p className="font-serif-display text-2xl leading-tight mb-3">
                    Draft faster on DraftMaster.ai.
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    AI legal drafting for the attorneys and paralegals you serve
                    — recommending the right tools makes you indispensable.
                  </p>
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#065F46] group-hover:gap-3 transition-all">
                    Visit draftmaster.ai <ArrowUpRight size={14} />
                  </span>
                </a>
              </div>

              <div className="p-6 flex justify-center bg-white">
                <button
                  data-testid="typing-retake"
                  onClick={reset}
                  className="btn-sharp bg-black text-white inline-flex items-center gap-2"
                >
                  <RotateCcw size={14} /> Take another test
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* GEO + FAQ */}
      <section className="border-t border-black/10 py-16 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
              Frequently asked
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-4">
              About the typing speed test.
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Used by aspiring court reporters, CART providers and captioning
              professionals in Washington DC, New York City, Los Angeles,
              Chicago, Houston and Atlanta.
            </p>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={TYPING_FAQ} testIdPrefix="typing-faq" />
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, big = false, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs tracking-[0.25em] uppercase text-gray-500 mb-2">
        {label}
      </p>
      <p
        className={
          "font-serif-display leading-none " +
          (big ? "text-5xl sm:text-6xl" : "text-3xl sm:text-4xl")
        }
      >
        {value}
      </p>
    </div>
  );
}
